/**
 * Pure month-grid maths for the calendar view. Civil-date only (`yyyy-MM-dd`) — no timezone, no date
 * library (ADR-0013): the venue-local "today" anchor is resolved once by the caller, everything here
 * is arithmetic over calendar dates. Weeks start Monday (FR convention).
 */

function iso(y: number, m0: number, d: number): string {
  const mm = String(m0 + 1).padStart(2, "0");
  const dd = String(d).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}

function ym(anchor: string): { y: number; m: number } {
  const p = anchor.split("-");
  return { y: Number(p[0]), m: Number(p[1]) };
}

/** First and last day of the anchor's month, ISO. */
export function monthRange(anchor: string): { from: string; to: string } {
  const { y, m } = ym(anchor);
  const from = iso(y, m - 1, 1);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from, to: iso(y, m - 1, last) };
}

export interface CalendarCell {
  date: string;
  inMonth: boolean;
}

/** The month laid out as weeks of 7 cells, Monday-first, padded from the prior/next month. */
export function monthMatrix(anchor: string): CalendarCell[][] {
  const { y, m } = ym(anchor);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  // 0=Sun..6=Sat → shift so Monday=0.
  const lead = (first.getUTCDay() + 6) % 7;

  const cells: CalendarCell[] = [];
  for (let i = 0; i < lead; i++) {
    const d = new Date(Date.UTC(y, m - 1, 1 - (lead - i)));
    cells.push({ date: iso(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()), inMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) cells.push({ date: iso(y, m - 1, d), inMonth: true });
  while (cells.length % 7 !== 0) {
    const idx = cells.length - (lead + daysInMonth) + 1;
    const d = new Date(Date.UTC(y, m, idx));
    cells.push({ date: iso(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()), inMonth: false });
  }

  const weeks: CalendarCell[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/** Shift an ISO month anchor by ±1 month (keeps day 01). */
export function shiftMonth(anchor: string, delta: number): string {
  const { y, m } = ym(anchor);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return iso(d.getUTCFullYear(), d.getUTCMonth(), 1);
}

/** Sum density counts per service day into `{ [day]: total }`. */
export function totalsByDay(
  rows: ReadonlyArray<{ serviceDay?: string; count?: number }>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of rows) {
    if (!r.serviceDay) continue;
    out[r.serviceDay] = (out[r.serviceDay] ?? 0) + (r.count ?? 0);
  }
  return out;
}
